import { OWDataType } from "@multiweb/types/contracts";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { Event } from "../../../../Global/Base/Events";
import { ValidationErrorType } from "../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { IValidatable, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IPopupMenu } from "../IPopupMenu";
import { PopupMenu } from "../PopupMenu";
import { IControlInfoObject } from "../XGControls/ControlInfo";
import { Control } from "./Control";
import { ControlParams, IEditableControl, IMultiEdit } from "./ControlInterfaces";

//materska trida pro ovladaci prvek
export class EditableControl extends Control implements IEditableControl {
    //#region Events
    public valueChanged: Event<IEditableControl, any> = new Event<IEditableControl, any>();
    public isEditingChanged: Event<IEditableControl, any> = new Event<IEditableControl, any>();
    public enabledChanged: Event<IEditableControl, any> = new Event<IEditableControl, any>();
    public validityChanged: Event<IValidatable, IValidationResult> = new Event<IValidatable, IValidationResult>();
    public clicked: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    public getFocus: Event<IEditableControl, FocusEvent> = new Event<IEditableControl, FocusEvent>();
    public lostFocus: Event<IEditableControl, FocusEvent> = new Event<IEditableControl, FocusEvent>();
    public hover: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    public mouseEnter: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    public mouseLeave: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    //#endregion Events

    //#region Fields
    /** Nazev atributu z definice formulare */
    public attrName: string;

    private _dataType: OWDataType | string;
    public get dataType(): OWDataType | string {
        return this._dataType;
    }
    public set dataType(value: OWDataType | string) {
        this._dataType = value;
    }
    public innerElement: HTMLElement = null;
    /** Obsahuje hodnotu zda uz byla nactena nebo zmenena hodnota nebo ne (kvuli zmene viditelnosti) */
    public isValueLoaded: boolean = false;

    /** div na zobrazovani chyb */
    protected _errorElement: HTMLElement;
    protected _errorPopup: IPopupMenu;
    protected _isEditing: boolean = false;
    protected _isGuiValid: boolean = true; // Kontrola validnosti pole na gui
    protected _interactive: boolean = true;
    protected _enabledVisual: boolean = true;
    protected _hasFocus: boolean = false;
    protected _placeholderLocKey: string;

    /**Stav "Nejsou zadana povinna pole"*/
    private _isInRequiredInvalidState: boolean = false;
    private __language: string;
    private _postponedRequired: boolean;
    private _recommended: boolean;
    private _required: boolean;
    private _invalidLocKey: string;
    private _invalidMessage: string;
    private _errorType: ValidationErrorType;
    //#endregion Fields

    //#region Properties

    public override get element(): HTMLElement {
        return super.element;
    }

    public override set element(v: HTMLElement) {
        super.element = v;
        this._toggleVisualClasses();
    }

    public get controlValue(): any {
        throw "Unimplemented method EditableControl.controlValue";
    }

    public set controlValue(_v: any) {
        throw "Unimplemented method EditableControl.controlValue";
    }

    public setInitialControlValue(value: any): void {
        // Pozn. setInitialControlValue byla zavedena primárně pro komponenty přepsané do Reactu.
        // U původních/nereactích komponent je záměrně ponecháno původní chování.
        this.controlValue = value;
    }

    /**
     * Zobrazena hodnota v komponente
     */
    public get displayedValue(): ITextBuilder {
        // potomci, kteri vyuzivaji input mohou vyuzit tuto implementaci
        if (this.innerElement instanceof HTMLInputElement)
            return this.innerElement.value;

        // ostatni si musi naimplementovat svoji
        throw "Unimplemented property EditableControl.displayedValue";
    }

    public set displayedValue(v: ITextBuilder) {
        if (this.innerElement instanceof HTMLInputElement) {
            this.innerElement.value = v.toString();
            this.validate();
            return;
        }

        throw "Unimplemented property EditableControl.displayedValue";
    }

    public get isValid(): boolean {
        return this._isValid;
    }

    protected set _isValid(value: boolean) {
        this.element.classList.toggle("invalid", !value);
    }

    protected get _isValid(): boolean {
        return !this.element.classList.contains("invalid");
    }

    //#region Enabled
    public override set enabled(value: boolean) {
        if (!this.element) // disposed
            return;

        if (this._enabled !== value) {
            this._enabled = value;
            this.enabledChanged.trigger(this, { value: value });
        }
    }

    public override get enabled(): boolean {
        return this._enabled;
    }
    //#endregion Enabled

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
    }

    public get enabledVisual(): boolean {
        return this._enabledVisual;
    }

    public set enabledVisual(value: boolean) {
        this._enabledVisual = value;
        this._setEnabledInput(value);
        this._toggleVisualClasses();
    }

    protected get _isActive(): boolean {
        return this.isEditing && this.enabled;
    }

    /**
     * Pri vstupu/vystupu do editace nastavuje styly komponetam
     */
    //#region IsInRequiredInvalidState
    public get isInRequiredInvalidState(): boolean {
        return this._isInRequiredInvalidState;
    }

    //Nastaveni komponenty do stavu "Nejsou zadane povinne pole"
    public set isInRequiredInvalidState(value: boolean) {
        if (value == this._isInRequiredInvalidState)
            return;

        try {
            // zmena povinnosti neni standartni zmena validnosti kvuli uzivatelskemu vstupu - pridavaly by se do VM duplicitne jako validacni chyba uzivatelskeho vstupu
            // jedine by se musel ve validityChanged posilat i typ
            this.validityChanged.enabled = false;

            if (value) {
                //Prepnuti koponenty do chybneho stavu
                this.setInvalid("GUI-55627");
                this._isInRequiredInvalidState = true;
            } else {
                if (this.controlValue != "") {
                    //Prepnuti komponenty zpet do validniho stavu
                    this.setValid();
                    //this.errElem.style.display = 'none';
                    this._isInRequiredInvalidState = false;
                } else
                    this._isInRequiredInvalidState = true;
            }
        } finally {
            this.validityChanged.enabled = true;
        }
    }
    //#endregion IsInRequiredInvalidState

    //#region Language
    /**
     * Obsluhuje hodnotu nastavenou v data-language atributu
     */
    protected get _language(): string {
        return this.__language;
    }

    /**
     * Obsluhuje hodnotu nastavenou v data-language atributu
     */
    protected set _language(lang: string) {
        this.__language = lang;
    }
    //#endregion Language

    //#region PostponedRequired
    public set postponedRequired(value: boolean) {
        if (this.postponedRequired === value)
            return;

        this.element.classList.toggle("PostponedRequired", value);
        if (this._captionLabel)
            this._captionLabel.classList.toggle("PostponedRequired", value);

        this._postponedRequired = value;
    }

    public get postponedRequired(): boolean {
        return this._postponedRequired;
    }
    //#endregion PostponedRequired

    /** Promenna ktera obsahuje informaci o tom, zda je v editacnim rezimu nebo ne */
    public set isEditing(value: boolean) {
        const oldVal = this._isEditing;
        this._isEditing = value;
        if (oldVal != value)
            this.isEditingChanged.trigger(this, { value: value });
    }

    public get isEditing(): boolean {
        return this._isEditing;
    }

    //#region Recommended
    public set recommended(value: boolean) {
        if (this.recommended === value)
            return;

        this.element.classList.toggle("Recommended", value);
        if (this._captionLabel)
            this._captionLabel.classList.toggle("Recommended", value);

        this._recommended = value;
    }

    public get recommended(): boolean {
        return this._recommended;
    }
    //#endregion Recommended

    //#region Required
    public set required(value: boolean) {
        if (this.required === value)
            return;

        this.element.classList.toggle("Required", value);
        if (this._captionLabel)
            this._captionLabel.classList.toggle("Required", value);

        this._required = value;
        this.validate();
    }

    public get required(): boolean {
        return this._required;
    }
    //#endregion Required

    //#region Visible
    public override set visible(value: boolean) {
        if (this.element == undefined)
            return;

        const oldVal = this._visible;
        this._visible = value;

        const display = value ? "" : "none";
        if (this._hasContainerParent) // parenta chceme zobrazit/schovat pouze pokud je to container
            this.element.parentElement.style.display = display;
        this.element.style.display = display;

        if (oldVal != value)
            this.visibilityChanged.trigger(this, value);
        else
            this.tryTriggerCanValueLoadChange(this, this.canValueLoad);//i pokud nedošlo ke změně viditelnosti, mohlo dojít ke změně canValueLoad, díky změně viditelnosti předka v DOM
    }

    public override get visible(): boolean {
        return this._visible;
    }
    //#endregion Visible

    //#endregion Properties

    //#region Constructors
    constructor(params: ControlParams) {
        super(params);

        if (this.element instanceof HTMLInputElement)
            this.element.setAttribute("autocomplete", "off");
        else
            this.element.tabIndex = -1;

        this.element.classList.add("EditableControl");
        this._toggleVisualClasses();

        this._errorElement = document.createElement("div");
        this._errorElement.classList.add("errDiv");

        this._placeholderLocKey = this.element.getAttribute("data-placeholderlc");

        if (this.element.hasAttribute("data-language"))
            this._language = this.element.getAttribute("data-language"); //nastaveni jazyka pro kontrolu pravopisu language/data-lang

        this._events.add(this.valueChanged, () => {
            //Pri zmene hodnoty vyvola kontrolu jestli se ma komponenta vratit do validniho. (Po tom co byla ve stavu: Nezadane povinne pole)
            this.isInRequiredInvalidState = false;
            this.isValueLoaded = true;
        });

        // zakazani/povoleni udalosti valueChanged
        this._events.add(this.canValueLoadChanged, (_, value) => this.valueChanged.enabled = value);

        this._events.add(this.clicked, (sender: EditableControl, event) => this._editableControlClicked(sender, event));
        this._events.add(this.getFocus, (sender: EditableControl, event) => this._editableControlGetFocus(sender, event));
        this._events.add(this.lostFocus, (sender: EditableControl, event) => this._editableControlLostFocus(sender, event));
        this._events.add(this.hover, (sender: EditableControl, event) => this._editableControlHover(sender, event));
        this._events.add(this.mouseEnter, (sender: EditableControl, event) => this._editableControlMouseEnter(sender, event));
        this._events.add(this.mouseLeave, (sender: EditableControl, event) => this._editableControlMouseLeave(sender, event));

        this._events.addListener(this.element, "click", e => this.clicked.trigger(this, e));
        this._events.addListener(this.element, "blur", e => this.lostFocus.trigger(this, e));
        this._events.addListener(this.element, "focus", e => {
            this.getFocus.trigger(this, e);
            this.dropDownElementPosition(this._errorPopup, this._errorElement);
        });

        this._events.addListener(this.element, "mouseover", e => {
            this.hover.trigger(this, e);
            this.dropDownElementPosition(this._errorPopup, this._errorElement);
        });

        this._events.addListener(this.element, "mouseenter", e => {
            this.mouseEnter.trigger(this, e);
            this.dropDownElementPosition(this._errorPopup, this._errorElement);
        });

        this._events.addListener(this.element, "mouseleave", e => {
            this.mouseLeave.trigger(this, e);
            this.dropDownElementPosition(this._errorPopup, this._errorElement);
        });
    }
    //#endregion Constructors

    //#region Methods

    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>): Promise<Partial<IControlInfoObject>> {
        return {
            ...childInfo,
            attributeName: this.attrName,
            locKey: this.locKey,
            additionalInfo: {
                dataAttributes: { ...this._getDataAttributes() },
                implementationClass: this.constructor.name,
            },
        };
    }

    public hasFocus(): boolean {
        return this._hasFocus;
    }

    protected _showErrorPopup(): void {
        if (this._isEditing && this._errorElement.hasChildNodes()) { // Nemusí existovat žádný obsah ke zobrazí = prázdný tooltip
            if (!this._errorPopup)
                this._errorPopup = new PopupMenu(this._errorElement, false); // this.Element.clientWidth + "px", "150px");

            this.dropDownElementPosition(this._errorPopup, this._errorElement);
            this._errorPopup.setVisibility(true);
        }
    }

    protected _hideErrorPopup(): void {
        if (this._errorPopup)
            this._errorPopup.setVisibility(false);
    }

    protected _editableControlClicked(_sender: EditableControl, _event: MouseEvent): void {
        // Prázdná metoda
    }

    /**
     * Nastaví CSS třídy na prvku podle aktivnosti.
     * @see {@link _enabledVisual}
     * @private
     */
    private _toggleVisualClasses(): void {
        this.element?.classList.toggle("Enabled", this._enabledVisual);
        this.element?.classList.toggle("Disabled", !this._enabledVisual);
    }

    private _editableControlGetFocus(_sender: IEditableControl, _event: FocusEvent): void {
        this._hasFocus = true;
    }

    private _editableControlLostFocus(_sender: IEditableControl, _event: FocusEvent): void {
        this._hasFocus = false;
        this._hideErrorPopup();
    }

    private _editableControlHover(_sender: IEditableControl, _event: MouseEvent): void {
        // Prázdná metoda
    }

    private _editableControlMouseEnter(_sender: IEditableControl, _event: MouseEvent): void {
        if (!this.isValid)
            this._showErrorPopup();
    }

    private _editableControlMouseLeave(_sender: IEditableControl, _event: MouseEvent): void {
        this._hideErrorPopup();
    }

    /** Nastavi focus na input */
    public focus(options?: FocusOptions): void {
        if (this.innerElement && ("focus" in this.innerElement)) {
            this.innerElement.focus(options);
        }
        this.isInRequiredInvalidState = false;
    }

    /**
     * Kontrola jestli je vyplnene povidnne pole
     */
    public isFilledRequiredControl(): boolean {
        if (this instanceof IMultiEdit && this.multiEditActive) {
            return !this.touched || !(this.required && !this.controlValue);
        }
        return (this.required && !this.controlValue) ? false : true;
    }

    /**
     * Kontrola jestli je vyplnene doporucene pole
     */
    public isFilledRecommendedControl(): boolean {
        if (this instanceof IMultiEdit && this.multiEditActive) {
            return !this.touched || !(this.recommended && !this.controlValue);
        }
        return this.recommended && !this.controlValue ? false : true;
    }

    /**
     * Kontrola jestli je vyplnene postponed pole
     */
    public isFilledPostponedControl(): boolean {
        if (this instanceof IMultiEdit && this.multiEditActive) {
            return !this.touched || !(this.postponedRequired && !this.controlValue && this.visible);
        }
        return this.postponedRequired && !this.controlValue && this.visible /*&& !this.readOnly*/ ? false : true;
    }

    protected _setEnabledInput(value: boolean): void {
        if (this.innerElement && ("readOnly" in this.innerElement))
            (this.innerElement as any)["readOnly"] = !value;
        else if (this.innerElement && ("disabled" in this.innerElement))
            (this.innerElement as any)["disabled"] = !value;
    }

    /** Vraci true - krome Editu pouzitelne pro vsechny inputy */
    public validate(): boolean {
        this.setValid();
        return true;
    }

    //Pokud byla kompnenta ve stavu Invalid tak ji vradi do stavu Valid
    public setValid(): void {
        if (!this._isGuiValid)
            return;

        if (this._isValid)
            return;

        this._isValid = true;

        this._hideErrorPopup();

        this._isInRequiredInvalidState = false;
        this.validityChanged.trigger(this, { valid: true });
    }

    // Zrusi gui-invalid
    protected _setGuiValid(): void {
        this._isGuiValid = true;
        this.element.classList.remove("gui-invalid");
    }

    // Nastavi komponentu na gui-invalid, to je neproslo validaci na komponente
    protected _setGuiInvalid(): void {
        this._isGuiValid = false;
        this.element.classList.add("gui-invalid");
    }

    //Pokud zadam nevalidni hodnotu tak prepne komponentu do stavu Invalid
    public setInvalid(locKey: string, message: string = null, errorType: ValidationErrorType = null): void {
        if (message && this._errorElement) {
            this._errorElement.innerHTML = StringUtils.stringToHTMLText(message);
            if (this._errorPopup && this._errorPopup.visible)
                this.dropDownElementPosition(this._errorPopup, this._errorElement);
        } else if (locKey) {
            Localization.addLocDataAsync(this._errorElement, locKey).then((errorElement) => {
                if (this._errorPopup && this._errorPopup.visible)
                    this.dropDownElementPosition(this._errorPopup, errorElement); // je nutne zavolat kvuli prepoctu vysky pro nove nacteny lokalizacni retezec
            });
        }

        if (!this._isValid)
            return;

        this._isValid = false;
        this._invalidLocKey = locKey;
        this._invalidMessage = message;
        this._errorType = errorType;
        this.validityChanged.trigger(this, { valid: false, invalidValue: this.displayedValue, invalidLocKey: locKey, invalidMessage: message, errorType: errorType });
    }

    public get currentValidity(): IValidationResult {
        return this.isValid ? { valid: true } : { valid: false, invalidValue: this.displayedValue, invalidLocKey: this._invalidLocKey, invalidMessage: this._invalidMessage, errorType: this._errorType };
    }

    /**
     * Vytvoreni pickeru na vybirani hodnoty (pouziva napr. DateEdit pro ikonku kalendare)
     */
    protected _createValuePicker(): HTMLElement {
        return createElement({ type: "button", classes: "valuepicker" }) as HTMLInputElement;
    }

    /**
     * Zobrazeni elementu s chybou nad/pod dle dostupneho prostoru. Pokud neni dostatek prostoru nad ani pod, tak se zobrazi dole.
     * @param popUpMenu - dropDown menu, ktere se bude vykreslovat
     * @param popUpElement - element do ktereho se vykresluje obsah pupupOkna
     * @param width - preddefinovana defaultni sirka. Pouziva se u LOV, protoze grid ma pred doctenim nulovou vysku a drop down menu se zobrazovalo jen jako tecka
     * @param height - preddefinovana defaultni vyska. Pouziva se u LOV, protoze grid ma pred doctenim nulovou vysku a drop down menu se zobrazovalo jen jako tecka
     */
    public dropDownElementPosition(popUpMenu: IPopupMenu, popUpElement: HTMLElement, gridRect: ClientRect = null, width: number = null, height: number = null): void {
        if (popUpElement != undefined && popUpMenu)
            popUpMenu.arangePopupToEditableControl(popUpElement, this.element, this.hasFocus(), gridRect, width, height);
    }

    /**
     * Výchozí implementace pro zahájení editace ({@link IStartEdit}) je zaměření komponenty. To je vhodné řešení pro většinu komponent založených na input elementu.
     * Komponenty, pro které není tato implementace dostatečná, poskytnou vlastní implementaci.
     */
    public startEdit(): void {
        this.focus();
    }
    //#endregion Methods

    //#region dispose
    public override dispose(): void {
        if (this._errorPopup)
            this._errorPopup.dispose();

        super.dispose();
    }

    //#endregion dispose
}
